import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { authType } from '../../../constants/auth-type';
import { BehaviorSubject, catchError, from, Observable, Subject, switchMap, tap, throwError } from 'rxjs';
import { cookieVariables } from '../../../constants/cookie-variables.constants';
import { CustomerService } from '../customer/customer.service';
import { NativeAppService } from '@shared/common/services/native-app/native-app.service';
import { TrackingNewService } from '../tracking/tracking-new.service';
import { LocationService } from '../location/location.service';
import { LogoutParams } from '../../interfaces/auth-params';
import { SentryLogService } from '@shared/common/services/sentry-log/sentry-log.service';
import { AuthService as SharedAuthService } from '@shared/features/authentication/services/auth.service';
import { UserAccount } from '@shared/features/account/interfaces/customer.interface';
import { CookieAppService } from '@shared/common/services/cookie-app/cookie-app.service';
import { AccountApi } from '@limehome/account-api-client';

@Injectable({
  providedIn: 'root'
})
export class AuthService extends SharedAuthService {
  isLoggedInSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  isOnlyLogin: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
  passedErrorMessage: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  openAuthModal: Subject<authType> = new Subject<authType>();

  constructor(
    cookieAppService: CookieAppService,
    private readonly customerService: CustomerService,
    public trackingNewService: TrackingNewService,
    private readonly nativeAppService: NativeAppService,
    private readonly router: Router,
    private readonly locationService: LocationService,
    private readonly sentryLogService: SentryLogService,
    accountApi: AccountApi
  ) {
    super(cookieAppService, accountApi);
  }

  logIn(email: string, password: string): Observable<UserAccount> {
    return this.signIn(email, password).pipe(
      switchMap((response) => {
        this.setToken(response.access_token);

        return from(this.customerService.getCustomer()).pipe(
          tap((account) => {
            this.handleLoggedInState(account);
          })
        );
      }),
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  checkIsAuthenticated(): void {
    if (this.isAuthenticated) {
      this.isLoggedInSubject.next(true);
      // Triggers call to retrieve User data, in case 401 then it is handled by interceptor
      this.customerService.getCustomer().subscribe();
    }
  }

  logOut({ withRedirect = true, callLogoutEndpoint = true } = {} as LogoutParams) {
    this.nativeAppService.notifyIntentToLogout();

    if (callLogoutEndpoint) {
      this.signOut().subscribe({
        next: () => this.processLogOut(withRedirect),
        error: (error) => {
          this.processLogOut(withRedirect, error);
        }
      });
    } else {
      this.processLogOut(withRedirect);
    }
  }

  processLogOut(withRedirect: boolean, error = null) {
    this.clearAuth();
    if (withRedirect) this.redirectToHomepage();
    if (error) this.reportErrorOnLogout(error);
  }

  reportErrorOnLogout(error: HttpErrorResponse) {
    this.sentryLogService.captureMessage({
      message: `Request to /logout failed with status ${error?.status || 'unknown'}`,
      severityLevel: 'warning',
      error
    });
  }

  handleLoggedInState(account: UserAccount) {
    this.isLoggedInSubject.next(true);
    this.sentryLogService.setTagIsLoggedIn(true);
    this.cookieAppService.removeCookie(cookieVariables.newsletterOnlyUser);
    this.trackingNewService.trackLoginEvent({
      page_name: this.trackingNewService.getTrackingPageName(this.router.url),
      user_id: account.id,
      is_subscribed_to_newsletter: account.newsletter
    });
  }

  clearAuth() {
    this.removeToken();
    this.customerService.dispatchAddCustomerInfo(null);
    this.isLoggedInSubject.next(false);
    this.cookieAppService.removeCookie(cookieVariables.isUserInternal);
    this.sentryLogService.setTagIsLoggedIn(false);
  }

  redirectToHomepage() {
    const currentUrl = this.router.url;
    const isAccountOrMyBookingsPage = currentUrl.indexOf('/account') > -1 || currentUrl.indexOf('/my-bookings') > -1;
    if (isAccountOrMyBookingsPage) {
      this.locationService.redirectToBrowserOnly('/');
    }
  }
}
